import {
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { FC, useCallback } from 'react';
import { DICTIONARY } from 'src/constants';
import { itemNameValidationSchema } from 'src/features/common/components/createItem/validation/ItemNameValidationSchema';
import { Disclosure } from 'src/features/common/models';
import { useCreateItemModal } from 'src/features/shared/createItem/hooks';
import { CreateItemButtonProps } from 'src/features/shared/createItem/models';

interface Values {
  name: string;
}

export const CreateItemModal: FC<Disclosure & CreateItemButtonProps> = ({ onClose, option, type, parent, onCreateSuccess }) => {
  const { createItem, isLoading } = useCreateItemModal(option.templateId, parent, onClose, onCreateSuccess);

  const onFormSubmit = useCallback(
    async (value: Values, { setSubmitting }: FormikHelpers<Values>) => {
      setSubmitting(true);
      await createItem(value.name, type);
    },
    [createItem, type],
  );

  return (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent onKeyDown={(e) => e.stopPropagation()}>
        <ModalHeader>
          <T _str={type === 'child' ? DICTIONARY.NEW_CHILD : DICTIONARY.NEW_SIBLING} insertOptionsName={option.name} />
        </ModalHeader>
        <ModalCloseButton />
        <Formik initialValues={{ name: '' }} validationSchema={itemNameValidationSchema} onSubmit={onFormSubmit}>
          <Form>
            <ModalBody>
              <Field name="name">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.name && form.touched.name}>
                    <FormLabel>
                      <T _str={DICTIONARY.NAME} />
                    </FormLabel>
                    <Input
                      {...field}
                      autoFocus
                      onBlur={() => form.setStatus('touched')}
                      disabled={form.isSubmitting}
                      data-testid="create-item-name-input"
                    />
                    <FormHelperText>
                      <T _str={DICTIONARY.RENAME_ITEM_NAME_DESCRIPTION} />
                    </FormHelperText>
                    {form.errors.name && form.touched.name && (
                      <Flex data-testid="create-item-error-message">
                        <FormErrorMessage>
                          <FormErrorIcon />
                          <T _str={form.errors.name} />
                        </FormErrorMessage>
                      </Flex>
                    )}
                  </FormControl>
                )}
              </Field>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button variant="ghost" data-testid="cancelBtn" onClick={onClose}>
                  <T _str={DICTIONARY.CANCEL} />
                </Button>
                <Button type="submit" data-testid="submit-btn" isDisabled={isLoading}>
                  <T _str={DICTIONARY.CREATE} />
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </Form>
        </Formik>
      </ModalContent>
    </Modal>
  );
};
